// DepartmentsPage.jsx
import React, { useEffect, useRef } from 'react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { Card, CardContent } from './ui/card';
import { Building2, Users, Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const DepartmentsPage = () => {
  const deptRef = useRef(null);
  const navigate = useNavigate();

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = deptRef.current?.querySelectorAll('.dept-card');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleViewDetails = () => {
    // Navigate to home page and then scroll to contact section
    navigate('/');
    // Wait for navigation then scroll to contact section
    setTimeout(() => {
      const element = document.querySelector('#contact');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const departments = [
    {
      name: 'Emergency Department',
      image: '/images/department/emergency.jpg',
      description: '24/7 emergency medical care with rapid response teams and advanced life support systems for critical situations.',
      doctors: 8,
      waitingTime: 'Immediate',
      features: ['Trauma Care', 'Critical Care', 'Emergency Surgery', 'Ambulance Service']
    },
    {
      name: 'General Medicine',
      image: '/images/department/general.jpg',
      description: 'Comprehensive primary healthcare for adults, managing common illnesses and chronic conditions.',
      doctors: 6,
      waitingTime: '15-20 mins',
      features: ['Health Check-ups', 'Chronic Disease Management', 'Preventive Care', 'Medical Consultations']
    },
    {
      name: 'Diabetology',
      image: '/images/department/diabetology.jpg',
      description: 'Specialized care for diabetes management, prevention, and treatment of diabetes-related complications.',
      doctors: 3,
      waitingTime: '20-25 mins',
      features: ['Diabetes Management', 'Insulin Therapy', 'Complication Prevention', 'Lifestyle Counseling']
    },
    {
      name: 'Orthopedics',
      image: '/images/department/orthopedic.jpg',
      description: 'Advanced treatment for bone, joint, and musculoskeletal conditions with modern surgical techniques.',
      doctors: 4,
      waitingTime: '25-30 mins',
      features: ['Joint Replacement', 'Fracture Care', 'Sports Medicine', 'Arthroscopy']
    },
    {
      name: 'Psychiatry',
      image: '/images/department/Psychiatry.jpg',
      description: 'Comprehensive mental health services for diagnosis, treatment, and management of psychiatric disorders.',
      doctors: 3,
      waitingTime: '20-25 mins',
      features: ['Counseling', 'Medication Management', 'Therapy Sessions', 'Mental Health Support']
    },
    {
      name: 'Pediatrics',
      image: '/images/department/Pediatric.jpg',
      description: 'Specialized healthcare for infants, children, and adolescents in a child-friendly environment.',
      doctors: 5,
      waitingTime: '10-15 mins',
      features: ['Vaccination', 'Growth Monitoring', 'Child Development', 'Pediatric Care']
    },
    {
      name: 'General Surgery',
      image: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      description: 'Comprehensive surgical care for various conditions using minimally invasive and traditional techniques.',
      doctors: 4,
      waitingTime: '30-40 mins',
      features: ['Laparoscopic Surgery', 'Gastrointestinal Surgery', 'Minimal Access Surgery', 'Surgical Oncology']
    },
    {
      name: 'ENT',
      image: '/images/department/ent.jpg',
      description: 'Comprehensive care for ear, nose, and throat disorders with advanced diagnostic and treatment facilities.',
      doctors: 3,
      waitingTime: '15-20 mins',
      features: ['Hearing Tests', 'Endoscopy', 'Sinonasal Surgery', 'Voice Disorder Treatment']
    },
    {
      name: 'Ophthalmology',
      image: '/images/department/Ophthalmology.jpg',
      description: 'Complete eye care services including diagnosis, treatment, and surgery for various eye conditions.',
      doctors: 3,
      waitingTime: '20-25 mins',
      features: ['Cataract Surgery', 'Retina Care', 'Glaucoma Treatment', 'Refractive Surgery']
    },
    {
      name: 'Plastic Surgery',
      image: '/images/department/plastic.jpg',
      description: 'Reconstructive and aesthetic surgical procedures to restore form and function.',
      doctors: 2,
      waitingTime: '30-40 mins',
      features: ['Reconstructive Surgery', 'Cosmetic Procedures', 'Burn Care', 'Microsurgery']
    },
    {
      name: 'Obstetrics & Gynecology',
      image: '/images/department/Obstetrics.jpg',
      description: 'Comprehensive women healthcare services from adolescence through menopause and beyond.',
      doctors: 4,
      waitingTime: '15-20 mins',
      features: ['Maternity Care', 'Gynecological Surgery', 'Fertility Treatment', 'Women Wellness']
    },
    {
      name: 'Anaesthesia',
      image: '/images/department/Anesthesia.jpg',
      description: 'Advanced anesthesia services for surgical procedures and pain management across all specialties.',
      doctors: 4,
      waitingTime: 'As per surgery',
      features: ['General Anesthesia', 'Regional Anesthesia', 'Pain Management', 'Critical Care']
    },
    {
      name: 'Dermatology',
      image: '/images/department/Dermatologist.jpg',
      description: 'Comprehensive care for skin, hair, and nail disorders with medical and cosmetic treatments.',
      doctors: 2,
      waitingTime: '20-25 mins',
      features: ['Skin Biopsy', 'Laser Therapy', 'Acne Treatment', 'Cosmetic Dermatology']
    },
    {
      name: 'Uro-Surgery',
      image: '/images/department/uro.jpg',
      description: 'Specialized surgical care for urinary tract and male reproductive system disorders.',
      doctors: 2,
      waitingTime: '25-30 mins',
      features: ['Urological Surgery', 'Stone Treatment', 'Prostate Care', 'Uro-oncology']
    },
    {
      name: 'Physiotherapy',
      image: '/images/department/Physiotherapy.jpg',
      description: 'Rehabilitation services for recovery from injuries, surgeries, and management of chronic conditions.',
      doctors: 3,
      waitingTime: '10-15 mins',
      features: ['Physical Therapy', 'Rehabilitation', 'Pain Management', 'Mobility Training']
    }
  ];

  return (
    <>
      <Navbar />
      <section className="pt-24 pb-16 bg-[hsl(var(--background))]" ref={deptRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center space-x-2 bg-[hsl(var(--primary))]/10 px-4 py-2 rounded-full">
              <Building2 className="w-5 h-5 text-[hsl(var(--primary))]" />
              <span className="text-sm font-semibold text-[hsl(var(--primary))]">
                Our Departments
              </span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--foreground))]">
              Specialized Medical
              <span className="block mt-2 text-gradient">Departments</span>
            </h2>
            <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              Our specialized medical departments work together to provide comprehensive,
              coordinated care for all your health needs with expert medical professionals.
            </p>
          </div>

          {/* Departments Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {departments.map((dept, index) => (
              <div
                key={index}
                className="dept-card opacity-0"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <Card className="group overflow-hidden hover:shadow-[var(--shadow-xl)] transition-all duration-500 border-none h-full bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--muted))]">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={dept.image}
                      alt={dept.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1559757175-0eb30cd8c063?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--foreground))]/90 via-[hsl(var(--foreground))]/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-xl font-bold text-white">
                        {dept.name}
                      </h3>
                    </div>
                  </div>
                  
                  <CardContent className="p-4">
                    <p className="text-[hsl(var(--muted-foreground))] text-sm mb-4 line-clamp-3">
                      {dept.description}
                    </p>
                    
                    <div className="flex items-center justify-between text-xs mb-4">
                      <div className="flex items-center space-x-2">
                        <Users className="w-3 h-3 text-[hsl(var(--primary))]" />
                        <span className="text-[hsl(var(--muted-foreground))]">{dept.doctors} Doctors</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-3 h-3 text-[hsl(var(--primary))]" />
                        <span className="text-[hsl(var(--muted-foreground))]">{dept.waitingTime}</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <h4 className="text-sm font-semibold text-[hsl(var(--foreground))]">Services:</h4>
                      <ul className="text-xs text-[hsl(var(--muted-foreground))] space-y-1">
                        {dept.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-center space-x-2">
                            <div className="w-1 h-1 bg-[hsl(var(--primary))] rounded-full flex-shrink-0"></div>
                            <span className="truncate">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button 
                      className="w-full flex items-center justify-center space-x-2 text-[hsl(var(--primary))] hover:text-[hsl(var(--primary-dark))] transition-colors text-sm font-medium py-2 rounded-lg bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/20"
                      onClick={handleViewDetails}
                    >
                      <span>View Details</span>
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Department Statistics */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] rounded-2xl p-6 text-white">
              <div className="text-2xl font-bold">{departments.length}+</div>
              <div className="text-sm opacity-90">Specialized Departments</div>
            </div>
            <div className="bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--muted))] rounded-2xl p-6">
              <div className="text-2xl font-bold text-[hsl(var(--primary))]">50+</div>
              <div className="text-sm text-[hsl(var(--muted-foreground))]">Expert Doctors</div>
            </div>
            <div className="bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--muted))] rounded-2xl p-6">
              <div className="text-2xl font-bold text-[hsl(var(--primary))]">24/7</div>
              <div className="text-sm text-[hsl(var(--muted-foreground))]">Emergency Care</div>
            </div>
            <div className="bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--secondary))] rounded-2xl p-6 text-white">
              <div className="text-2xl font-bold">100%</div>
              <div className="text-sm opacity-90">Patient Satisfaction</div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};